<script>
import { uniqueId } from "lodash";
import {
  criteriaChecker,
  MAX_COUNT_WORDS,
  getWordsCountOfAllText,
} from "./utils";
import EgIcon from "@/components/global/eg/eg_icon.vue";
import RubricPreviewTables from "@/components/rubrics/rubric_preview_tables.vue";
import deleteConfirmationModalMixin from "@/mixins/deleteConfirmationModal";
import ExpandableTextarea from "@/components/rubrics/rubricForm/ExpandableTextarea.vue";
import RubricBuilderCreateHeader from "@/components/rubrics/rubricForm/rubric_builder_create_header.vue";
import scrollToElement from "@/utils/scrollToEl.js";
import NewRubricView from "@/views/new_rubric_view.vue";
import { RUBRIC_CREATION_OPTIONS } from "@/constants";

const getDefaultCriteria = () => ({
  point: "",
  scale: 4,
  scales: [
    {
      id: uniqueId("temp_"),
      description: "",
      scale: 4,
    },
  ],
});

const getDefaultFormData = () => ({
  name: "",
  type: "Advanced",
  intensity: "moderate",
  criteria: [getDefaultCriteria()],
  flexibleScoring: false,
});

const normalizeRubricData = (rubricData) => {
  if (!rubricData || !rubricData.name) {
    return getDefaultFormData();
  }

  const normalized = {
    name: rubricData.name || "",
    type: rubricData.type || "Advanced",
    intensity: rubricData.intensity || "moderate",
    criteria: [getDefaultCriteria()],
    flexibleScoring:
      rubricData.flexibleScoring ?? getDefaultFormData().flexibleScoring,
  };

  if (!rubricData.criterias || !Array.isArray(rubricData.criterias)) {
    return normalized;
  }

  normalized.criteria = rubricData.criterias.map((criteria) => {
    if (!criteria.criteriaGrades || !Array.isArray(criteria.criteriaGrades)) {
      return getDefaultCriteria();
    }

    return {
      point: criteria.name || "",
      scale: 0,
      scales: criteria.criteriaGrades.map((grade) => ({
        id: grade.id || uniqueId("temp_"),
        description: grade.description || "",
        scale: grade.maxGrade || 0,
        title: grade.title || "",
      })),
    };
  });

  return normalized;
};

export default {
  name: "RubricForm",
  components: {
    ExpandableTextarea,
    RubricBuilderCreateHeader,
    EgIcon,
    RubricPreviewTables,
  },
  mixins: [deleteConfirmationModalMixin],
  props: {
    noButtons: {
      type: Boolean,
      default: false,
    },
    hideActions: {
      type: Boolean,
      default: false,
    },
    editing: {
      type: Boolean,
      required: false,
    },
    rubric: {
      type: Object,
      required: false,
      default: () => null,
    },
    previewMode: {
      type: Boolean,
      default: false,
    },
    initialData: {
      type: Object,
      required: false,
      default: () => null,
    },
  },
  data() {
    const hasRubric = this.rubric && this.rubric.name;
    const hasInitialData = this.initialData && this.initialData.name;
    const source = hasRubric
      ? this.rubric
      : hasInitialData
      ? this.initialData
      : null;
    const normalized = normalizeRubricData(source);

    return {
      canValidate: false,
      name: normalized.name,
      intensity: normalized.intensity,
      scaleType: normalized.type,
      criteria: normalized.criteria,
      isRubricLoaded: hasRubric || hasInitialData,
      flexibleScoring: normalized.flexibleScoring,
    };
  },

  computed: {
    form() {
      return {
        name: this.name,
        type: this.scaleType,
        intensity: this.intensity,
        criterias: this.getCriteriasForPreview(),
        flexibleScoring: this.flexibleScoring,
      };
    },
    buttonText() {
      return this.editing
        ? this.$t("rubrics.saveChanges")
        : this.$t("rubrics.createRubric");
    },
    canAddCriteriaPoint() {
      return this.criteria.length < 50;
    },
    isUsingCustomScale() {
      return ["Simple", "Advanced"].includes(this.scaleType);
    },
    isUsingAdvancedScale() {
      return this.scaleType === "Advanced";
    },
    duplicateScoreList() {
      const result = [];
      this.criteria.forEach((criterion, criterionIndex) => {
        const scores = criterion.scales.map(
          ({ scale: scoreValue }) => scoreValue
        );
        const duplicates = scores.filter(
          (score, index) => scores.indexOf(score) !== index
        );

        if (duplicates.length > 0) {
          const firstDuplicate = duplicates[0];
          const firstDuplicateIndex = scores.findIndex(
            (score, idx) =>
              score === firstDuplicate && scores.indexOf(score) !== idx
          );

          result.push({
            criterionIndex,
            duplicates: new Set(duplicates),
            firstDuplicateIndex,
          });
        }
      });
      return result;
    },
    hasCriteriaWithSingleScoreLevel() {
      if (!this.isUsingCustomScale) return false;
      return this.criteria.some(
        (criterion) => criterion.scales && criterion.scales.length === 1
      );
    },
    duplicateCriteriaNameSet() {
      const names = this.criteria.map((c) => c.point?.trim().toLowerCase());
      const seen = new Set();
      const duplicates = new Set();
      names.forEach((name) => {
        if (name) {
          if (seen.has(name)) duplicates.add(name);
          else seen.add(name);
        }
      });
      return duplicates;
    },
  },
  watch: {
    rubric(newRubric) {
      if (newRubric && newRubric.name && !this.isRubricLoaded) {
        this.loadRubric();
        this.$nextTick(() => {
          this.emitFormData();
        });
      }
    },
    name() {
      this.emitFormData();
    },

    scaleType() {
      this.emitFormData();
    },
    intensity() {
      this.emitFormData();
    },
    flexibleScoring() {
      this.emitFormData();
    },
    criteria: {
      handler() {
        this.emitFormData();
      },
      deep: true,
    },
  },
  mounted() {
    // Always emit initial form data to parent, even for empty/default values
    this.$nextTick(() => {
      this.emitFormData();
    });
  },
  methods: {
    async handleSubmit() {
      await this.onSubmit();
    },
    emitFormData() {
      this.$emit("formDataUpdate", this.form);
    },
    // Emit event to switch to the first tab
    switchToFirstTab() {
      this.$emit("switchToFirstTab");
    },
    getCriteriasForPreview() {
      if (this.scaleType === "Default") {
        return this.criteria.map((criterion) => ({
          criteriaGrades: [
            {
              description: criterion.point,
            },
          ],
        }));
      }

      if (this.scaleType === "Simple") {
        return this.criteria.map((criterion) => ({
          criteriaGrades: [
            {
              description: criterion.point,
              maxGrade: criterion.scale || 0,
            },
          ],
        }));
      }

      if (this.scaleType === "Advanced") {
        return this.criteria.map((criterion) => ({
          name: criterion.point,
          criteriaGrades: criterion.scales.map((scale) => ({
            description: scale.description,
            maxGrade: scale.scale,
            title: scale.title,
            id: scale.id,
          })),
        }));
      }

      return [];
    },

    addCriteriaPoint() {
      if (this.canAddCriteriaPoint) {
        this.criteria.push(getDefaultCriteria());
      }
    },

    async deleteCriteria(index) {
      const shouldDelete = await this.showDeleteConfirmationModal({
        message: this.$t("rubrics.deleteCriteria"),
      });
      if (shouldDelete) {
        this.criteria.splice(index, 1);
      }
    },

    duplicateCriteria(index) {
      this.criteria.splice(index, 0, structuredClone(this.criteria[index]));
    },

    async addCriteriaDescription(index, scaleIndex) {
      this.criteria[index].scales.splice(scaleIndex + 1, 0, {
        description: "",
        scale: 0,
        title: "",
        id: uniqueId("temp_"),
      });
      this.$nextTick(() => {
        const ref = this.$refs[`c-${index}-s-${scaleIndex + 1}`][0];
        ref.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "center",
        });
      });
    },

    deleteCriteriaDescription(index, scaleIndex) {
      this.criteria[index].scales.splice(scaleIndex, 1);
    },

    checkWordsCountOfAllText() {
      if (getWordsCountOfAllText(this.criteria) > MAX_COUNT_WORDS) {
        this.$showToastError(
          "The maximum word count for a rubric is 25,000 words. Please reduce the word count on your rubric and try again."
        );
        return true;
      }

      return false;
    },

    // Check if a specific score is a duplicate within its criterion
    isDuplicateScore(criterionIndex, scaleScore) {
      if (!this.canValidate) return false;
      const item = this.duplicateScoreList.find(
        (d) => d.criterionIndex === criterionIndex
      );
      return item?.duplicates?.has(scaleScore) || false;
    },

    // Get validation state for score input (false = invalid, null = no validation)
    getScoreValidationState(criterionIndex, scaleScore) {
      if (!this.canValidate) return "default";

      return this.isDuplicateScore(criterionIndex, scaleScore)
        ? "error"
        : "default";
    },

    getScoreErrorMessage(criterionIndex, scaleScore) {
      if (!this.canValidate) return "";

      if (this.isDuplicateScore(criterionIndex, scaleScore)) {
        return this.$t("rubrics.duplicateScore");
      }

      return "";
    },

    // Check if criterion has less than 2 descriptors
    hasInsufficientDescriptors(criterionIndex) {
      if (!this.canValidate) return false;
      if (!this.isUsingCustomScale) return false;
      const criterion = this.criteria[criterionIndex];
      return criterion.scales && criterion.scales.length < 2;
    },

    handleNameUpdate(newName) {
      this.name = newName;
    },
    getCriterionState(criterionPoint) {
      if (!this.canValidate) {
        return "default";
      }

      const normalizedName = criterionPoint?.trim().toLowerCase();

      if (!normalizedName) {
        return "error";
      }

      if (this.duplicateCriteriaNameSet.has(normalizedName)) {
        return "error";
      }

      return "default";
    },
    handleFlexibleScoringUpdate(isFlexible) {
      this.flexibleScoring = Boolean(isFlexible);
    },

    validateForm() {
      if (this.isUsingCustomScale && this.duplicateScoreList.length > 0) {
        const { criterionIndex, firstDuplicateIndex } =
          this.duplicateScoreList[0];

        const refKey = this.isUsingAdvancedScale
          ? `score-${criterionIndex}-${firstDuplicateIndex}`
          : `criterion-name-${criterionIndex}`;
        const element = this.$refs[refKey]?.[0];

        if (element) {
          scrollToElement(element);
        }

        this.$showToastError({
          context: this,
          template: `Duplicate scores found.
          Each score level within a rubric criterion must be unique (e.g., instead of 1, 1, 1, 1, use 1, 2, 3, 4). Still unsure?
          Upload your rubric file [here](@click:openUploadOrImportTab), and we will automatically rebuild an AI-grading-friendly rubric for you.`,
        });

        return false;
      }

      if (this.duplicateCriteriaNameSet.size > 0) {
        const firstDuplicateIndex = this.criteria.findIndex(
          (c) =>
            c.point?.trim() &&
            this.duplicateCriteriaNameSet.has(c.point.trim().toLowerCase())
        );
        if (firstDuplicateIndex !== -1) {
          const ref = this.$refs[`criterion-name-${firstDuplicateIndex}`];
          if (ref && ref[0]) {
            scrollToElement(ref[0]);
          }
        }
        this.$showToastError(this.$t("rubrics.duplicateCriteriaNames"));
        return false;
      }

      const hasEmptyCriteria = this.criteria.filter((criteria, index) => {
        if (criteria.point?.trim().length === 0) {
          const ref = this.$refs[`criterion-name-${index}`];
          if (ref && ref[0]) {
            scrollToElement(ref[0]);
          }
          return true;
        }

        if (this.scaleType === "Simple") {
          if (!criteria.scale || criteria.scale <= 0) {
            const ref = this.$refs[`criterion-name-${index}`];
            if (ref && ref[0]) {
              scrollToElement(ref[0]);
            }
            return true;
          }
        }

        if (this.isUsingAdvancedScale) {
          // Check if criterion has at least one scale
          if (!criteria.scales || criteria.scales.length === 0) {
            const ref = this.$refs[`criterion-name-${index}`];
            if (ref && ref[0]) {
              scrollToElement(ref[0]);
            }
            return true;
          }

          return criteria.scales.some((scale, scaleIndex) => {
            if (scale.description?.trim().length === 0) {
              const ref = this.$refs[`c-${index}-s-${scaleIndex}`];
              if (ref && ref[0]) {
                scrollToElement(ref[0]);
              }
              return true;
            }
            return false;
          });
        }

        return false;
      });

      if (hasEmptyCriteria.length > 0) {
        this.$showToastError(this.$t("rubrics.fillCriteriaFields"));
        return false;
      }

      if (this.name?.trim().length === 0) {
        const rubricHeader = this.$refs.rubricHeader;
        if (rubricHeader && rubricHeader.$refs.nameInput) {
          scrollToElement(rubricHeader.$refs.nameInput);
        }
        this.$showToastError(this.$t("rubrics.nameRequired"));
        return false;
      }

      if (this.name.trim().length >= 255) {
        const rubricHeader = this.$refs.rubricHeader;
        if (rubricHeader && rubricHeader.$refs.nameInput) {
          scrollToElement(rubricHeader.$refs.nameInput);
        }
        this.$showToastError(this.$t("rubrics.nameTooLong"));
        return false;
      }

      return true;
    },

    openUploadOrImportTab() {
      this.$egModal.hide("modal-new-rubric-form");
      this.$egModal.hide("modal-rubric");
      if (this.$route.path !== "/rubrics") {
        this.$router.push("/rubrics");
      }

      this.$openForm("new-rubric-form", {
        forceComponent: NewRubricView,
        isModal: true,
        modalProps: {
          hideFooter: true,
          hideHeader: true,
          size: "content",
        },
        props: {
          isRedirectAfterCreated: false,
          initialTab: RUBRIC_CREATION_OPTIONS[0].creationType,
        },

        on: {
          close: () => {
            this.$egModal.hide("modal-new-rubric-form");
          },
        },
      });
    },

    // Additional validation checks after validateForm
    async validateRubricData() {
      if (this.checkWordsCountOfAllText()) {
        return false;
      }

      if (!this.name) {
        this.$showToastError(this.$t("rubrics.nameRequired"));
        return false;
      }

      if (this.name.length > 255) {
        this.$showToastError(
          "The rubric name is too long. The maximum is 255 characters."
        );
        return false;
      }

      if (!this.criteria[0].point || !this.criteria[0].point.trim()) {
        this.$showToastError({
          context: this,
          template: this.$t("rubrics.uploadOrImport"),
        });

        return false;
      }

      if (this.isUsingCustomScale) {
        // Enable flexible scoring if any criterion has only one score level
        const hasCriteriaWithSingleScoreLevel = this.criteria.some(
          (criterion) => criterion.scales && criterion.scales.length === 1
        );

        if (hasCriteriaWithSingleScoreLevel) {
          this.flexibleScoring = true;
        }
      }

      if (this.criteria.length === 1) {
        const shouldContinue = await this.$showConfirmationModal({
          title: this.$t("rubrics.areSureToContinue"),
          warningTitle: this.$t("rubrics.onlyCriteriaWarning"),
          warningText: this.$t("rubrics.onlyCriteriaText"),
          cancelTitle: this.$t("rubrics.continueBtn"),
          okTitle: this.$t("rubrics.editRubricBtn"),
        });

        if (!shouldContinue) {
          return false;
        }
      }

      return true;
    },

    // Prepare rubric data and emit submit event
    prepareAndSaveRubricData() {
      let criterias = this.criteria.map((criteria) => {
        const criteriaData = {};

        if (this.isUsingAdvancedScale) {
          criteriaData.name = criteria.point;
        }

        criteriaData.criteriaGrades = criteria.scales.map((scale) => ({
          description: this.isUsingAdvancedScale
            ? scale.description
            : criteria.point,
          maxGrade: this.isUsingAdvancedScale ? scale.scale : criteria.scale,
          title: scale.title,
        }));

        return criteriaData;
      });

      if (this.scaleType === "Default") {
        criterias = this.criteria.map((obj) => {
          return {
            criteriaGrades: [
              {
                description: obj.point,
              },
            ],
          };
        });
      }

      const rubricData = {
        name: this.name,
        type: this.scaleType,
        intensity: this.intensity,
        criterias,
        flexibleScoring: this.flexibleScoring,
      };

      const { canCreateRubric, rubricError } = criteriaChecker({
        scaleType: this.scaleType,
        criterias: rubricData.criterias,
      });

      if (!canCreateRubric) {
        this.$showToastError(rubricError);
        return false;
      }

      this.$emit("rubricSubmitted", rubricData);

      return rubricData;
    },

    // return data or false to detect error or data
    async onSubmit(e) {
      e && e.preventDefault();

      this.canValidate = true;

      await this.$nextTick();
      await this.$nextTick(); // Second tick to ensure child components update

      // Step 1: Validate form fields
      const isValid = this.validateForm();
      if (!isValid) {
        this.switchToFirstTab();
        return false;
      }

      // Step 2: Additional rubric data validation
      const isRubricDataValid = await this.validateRubricData();
      if (!isRubricDataValid) {
        this.switchToFirstTab();
        return false;
      }

      // Step 3: All validation passed, prepare and save rubric data
      return this.prepareAndSaveRubricData();
    },

    showDeleteConfirmation() {
      this.showDeleteConfirmationModal({
        message: this.$t("rubrics.deleteRubricConfirm"),
      })
        .then((needDelete) => {
          needDelete && this.$emit("delete");
        })
        .catch((err) => {
          this.$showToastError(this.$t("errors.genericError"), {
            title: this.$t("rubrics.deleteRubric"),
          });

          console.error(err);
        });
    },
    loadRubric() {
      if (!this.rubric || !this.rubric.name) {
        return;
      }

      this.isRubricLoaded = true;

      const normalized = normalizeRubricData(this.rubric);
      this.name = normalized.name;
      this.scaleType = normalized.type;
      this.intensity = normalized.intensity;
      this.criteria = normalized.criteria;
      this.flexibleScoring = normalized.flexibleScoring;
    },
  },
};
</script>

<template>
  <eg-b-form @submit="onSubmit">
    <div>
      <RubricBuilderCreateHeader
        v-if="!previewMode"
        ref="rubricHeader"
        :name="name"
        :can-validate="canValidate"
        :flexible-scoring="flexibleScoring"
        @update:name="handleNameUpdate"
        @update:flexible-scoring="handleFlexibleScoringUpdate"
      >
        <template #before-criteria>
          <slot name="before-criteria"></slot>
        </template>
      </RubricBuilderCreateHeader>
      <div
        v-if="!previewMode"
        class="rubric-criteria-title-container d-flex justify-content-between align-items-center"
      >
        <h2 class="heading-s">
          {{ previewMode ? "Rubric Criteria Preview" : "Rubric Criteria" }}
        </h2>
        <div class="d-flex align-items-center flex-gap-1">
          <eg-button
            v-if="!previewMode"
            type="primary"
            size="medium"
            icon="plus-add-more-maximize"
            icon-size="medium"
            data-testid="rubric-builder-add-criteria"
            @click="addCriteriaPoint"
          >
            {{ $t("rubrics.addCriteria") }}
          </eg-button>
        </div>
      </div>
      <div v-if="!previewMode" class="criteria-list d-flex">
        <div
          v-for="(criterion, index) in criteria"
          :key="index"
          class="criteria-container"
        >
          <div class="section-criterion-name d-flex align-items-center">
            <eg-input
              :ref="`criterion-name-${index}`"
              v-model="criterion.point"
              size="medium"
              :placeholder="$t('rubrics.criteriaNamePlaceholder')"
              class="pale-placeholder"
              :state="getCriterionState(criterion.point)"
              :show-validation="canValidate"
            />
            <div v-if="scaleType === 'Simple'">
              <eg-input
                :ref="`criterion-name-${index}`"
                v-model="criterion.scale"
                size="medium"
                type="number"
                number
                :min="0"
                :max="100"
                :step="0.1"
                class="grade-value"
                :state="getScoreValidationState(index, criterion.scale)"
                :show-validation="canValidate"
              />
              <template
                v-for="errorMsg in [
                  getScoreErrorMessage(index, criterion.scale),
                ]"
              >
                <eg-b-form-invalid-feedback v-if="errorMsg" :key="errorMsg">
                  {{ errorMsg }}
                </eg-b-form-invalid-feedback>
              </template>
            </div>
            <eg-button
              type="secondary"
              icon="plus-add-more-maximize"
              size="small"
              data-testid="rubric-builder-add-score"
              @click="
                () =>
                  addCriteriaDescription(
                    index,
                    criterion?.scales?.length - 1 || 0
                  )
              "
            >
              {{ $t("rubrics.addScoreLevel") }}
            </eg-button>
            <eg-button
              type="ordinary"
              size="small"
              icon="duplicate-copy"
              title="Duplicate criteria"
              icon-color="var(--color-icon-neutral-neutral)"
              icon-size="medium"
              @click="duplicateCriteria(index)"
            />
            <eg-button
              type="ordinary"
              size="small"
              icon="delete"
              icon-color="var(--color-icon-neutral-neutral)"
              title="Remove criteria descriptor"
              icon-size="medium"
              :disabled="criteria.length <= 1"
              @click="deleteCriteria(index)"
            />
          </div>
          <div v-if="scaleType === 'Advanced'" class="section-criterion-grades">
            <div
              v-for="(scale, scaleIdx) in criterion.scales"
              :key="scale.id"
              class="d-flex"
            >
              <div :ref="`c-${index}-s-${scaleIdx}`" class="criteria-grade">
                <div class="d-flex flex-gap">
                  <eg-input
                    v-model="scale.title"
                    size="medium"
                    :placeholder="$t('rubrics.descriptorPlaceholder')"
                    class="grade-description pale-placeholder flex-grow-1"
                    state="default"
                  />
                  <div>
                    <div class="d-flex align-items-center flex-gap">
                      <eg-input
                        :ref="`score-${index}-${scaleIdx}`"
                        v-model="scale.scale"
                        size="medium"
                        type="number"
                        number
                        :min="0"
                        :max="100"
                        :step="0.1"
                        class="grade-value"
                        :state="getScoreValidationState(index, scale.scale)"
                        :show-validation="canValidate"
                      />
                      <div class="caption-s">{{ $t("rubrics.pts") }}</div>
                    </div>
                    <template
                      v-for="errorMsg in [
                        getScoreErrorMessage(index, scale.scale),
                      ]"
                    >
                      <eg-b-form-invalid-feedback
                        v-if="errorMsg"
                        :key="errorMsg"
                      >
                        {{ errorMsg }}
                      </eg-b-form-invalid-feedback>
                    </template>
                  </div>
                  <eg-button
                    type="ordinary"
                    size="medium"
                    icon="delete"
                    icon-color="var(--color-icon-neutral-neutral)"
                    title="Remove"
                    icon-size="small"
                    @click="deleteCriteriaDescription(index, scaleIdx)"
                  />
                </div>
                <ExpandableTextarea
                  :value="scale.description"
                  :can-validate="canValidate"
                  @change="scale.description = $event"
                />
              </div>
              <div class="d-flex flex-column align-items-center">
                <div class="vertical-line" />
                <eg-button
                  :class="[
                    'add-descriptor-button',
                    {
                      'add-descriptor-button-error':
                        hasInsufficientDescriptors(index),
                    },
                  ]"
                  @click="() => addCriteriaDescription(index, scaleIdx)"
                >
                  <eg-icon
                    icon="add"
                    color="var(--color-icon-primary-primary)"
                    size="medium"
                  />
                </eg-button>
                <div class="vertical-line" />
              </div>
            </div>
            <div style="flex: 0 0 100px" />
          </div>
        </div>
        <eg-button
          v-if="!previewMode"
          type="secondary"
          size="small"
          icon="plus-add-more-maximize"
          icon-size="medium"
          class="add-criteria-bottom"
          @click="addCriteriaPoint"
        >
          {{ $t("rubrics.addCriteria") }}
        </eg-button>
      </div>

      <!-- Preview Tab -->
      <RubricPreviewTables
        v-else-if="previewMode"
        :type="scaleType"
        :criteria="criteria"
      />
    </div>

    <div v-if="!noButtons && !hideActions" class="mt-3 text-right">
      <eg-b-button class="mr-2" to="/rubrics">Cancel</eg-b-button>
      <eg-b-button
        v-if="editing"
        class="mr-2"
        variant="danger"
        @click="showDeleteConfirmation"
      >
        Delete
      </eg-b-button>
      <eg-b-button class="avoid-support-overlap" type="submit" variant="info">{{
        buttonText
      }}</eg-b-button>
    </div>
  </eg-b-form>
</template>

<style lang="scss" scoped>
.criteria-list {
  gap: 1rem;
  flex-direction: column;
  padding-left: 2px;
}
.criteria-container {
  padding: 1rem;
  border-radius: var(--radius-rd-400);
  border: 1px solid var(--color-stroke-neutral-default, #e5ebf0);
  box-shadow: -2px 0 0 0 #8a3ffc;
}
.criteria-container,
.section-criterion-grades {
  background: white;
}
.section-criterion-name {
  display: flex;
  gap: 1rem;
  border-radius: var(--radius-rd-400) var(--radius-rd-400) 0 0;
  .pale-placeholder {
    width: auto;
    flex-grow: 1;
  }
}
.rubric-criteria-title-container {
  position: sticky;
  top: -1.5rem;
  padding-top: 1.5rem;
  padding-bottom: 1.5rem;
  background-color: var(--color-surface-neutral-canvas);
  z-index: 1;
  h2 {
    margin-bottom: 0;
  }
}
.section-criterion-grades {
  border-top: 1px #dfdfdf solid;
  margin-top: 1rem;
  padding-top: 1rem;
  scrollbar-width: thin;
  display: flex;
  flex-wrap: wrap;
  gap: 20px 0;
}
.add-criteria-bottom {
  width: 100%;
  height: 32px;
}
.section-add-criterion {
  padding: 1rem;
  border: 0 1px 1px 1px solid var(--color-stroke-neutral-default, #e5ebf0);
  border-radius: 0 0 0.25rem 0.25rem;
}

.criteria-grade {
  display: inline-flex;
  flex-direction: column;
  width: 365px;
  height: 224px;
  gap: 0.5rem;
  flex: 0 0 auto;
  padding: 1rem;
  border-radius: var(--radius-rd-400, 12px);
  background: var(--color-surface-neutral-container-1, #f6f8fa);
  // TODO: temporary solution, need to replace elements with the new eg-input
  ::v-deep {
    input,
    textarea {
      border-color: var(--color-stroke-neutral-default);
    }
    .caption-s {
      color: var(--color-text-neutral-secondary);
      line-height: normal;
    }
  }
}

.criteria-grade .grade-description {
  width: 100px;
}

.grade-value {
  width: 65px;
}

.vertical-line {
  border-left: 1px #dfdfdf solid;
  height: 100%;
}
.rubric-criteria-scale-type {
  width: 120px;
  ::v-deep label {
    font-size: var(--typography-font-size-fs-300);
    line-height: 1.5rem;
  }
}
.add-descriptor-button {
  padding: 6px;
  line-height: 0;
  transition: box-shadow 0.2s ease;

  &.add-descriptor-button-error {
    box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.5);
    animation: pulse-error 2s ease-in-out infinite;
  }
}

@keyframes pulse-error {
  0%,
  100% {
    box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.5);
  }
  50% {
    box-shadow: 0 0 0 6px rgba(220, 38, 38, 0.3);
  }
}
</style>
